import { formatBytes } from "@/lib/formatters";
import { useContext, useEffect, useRef, useState, type ClipboardEvent } from "react";
import { CircleAlert, CircleCheck, Copy, Paperclip, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AuthContext } from "@/contexts/auth-context";
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENTS_BYTES,
  buildErrorReport,
  formatReportText,
  sendErrorReport,
  type ErrorReportResult,
} from "@/lib/error-report";

interface ReportProblemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** O que a pessoa viu (a frase do aviso). Sem isto, é um relatório livre e a mensagem é obrigatória. */
  title?: string;
  /** O pormenor técnico, se houver. */
  message?: string;
  /** Em que parte da app. */
  area?: string;
  context?: Record<string, unknown>;
}

type Status = "idle" | "sending" | "sent" | "failed";

const MIN_FREE_NOTE = 5;


/**
 * «Reportar um problema»: a pessoa escreve o que se passa, junta o que ajude
 * (uma captura de ecrã, o ficheiro que deu erro) e um clique manda tudo por email
 * à equipa — com o registo técnico da página e quem é. A equipa responde por email.
 * Se o email não sair, o relatório fica à mão para copiar: quem reporta nunca
 * fica sem forma de o fazer chegar.
 */
export const ReportProblemDialog = ({
  open,
  onOpenChange,
  title,
  message,
  area,
  context,
}: ReportProblemDialogProps) => {
  const user = useContext(AuthContext)?.user;
  const fileInput = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [result, setResult] = useState<ErrorReportResult | null>(null);
  const [fallbackText, setFallbackText] = useState("");
  const [copied, setCopied] = useState(false);

  // Cada abertura é um relatório novo.
  useEffect(() => {
    if (open) {
      setNote("");
      setFiles([]);
      setFileError(null);
      setStatus("idle");
      setResult(null);
      setFallbackText("");
      setCopied(false);
    }
  }, [open]);

  const isFree = !title;
  const canSend = status !== "sending" && (!isFree || note.trim().length >= MIN_FREE_NOTE);

  const addFiles = (incoming: File[]) => {
    if (incoming.length === 0) return;
    const next = [...files, ...incoming];
    const total = next.reduce((sum, f) => sum + f.size, 0);
    if (next.length > MAX_ATTACHMENTS) {
      setFileError(`Podes juntar no máximo ${MAX_ATTACHMENTS} ficheiros.`);
    } else if (total > MAX_ATTACHMENTS_BYTES) {
      setFileError(
        `Os ficheiros são grandes de mais (${formatBytes(total)}). O limite, no total, é ${formatBytes(MAX_ATTACHMENTS_BYTES)}.`,
      );
    } else {
      setFileError(null);
      setFiles(next);
    }
  };

  // Colar uma captura de ecrã (Ctrl+V) na mensagem junta-a como anexo.
  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const images = Array.from(event.clipboardData.files).filter((f) => f.type.startsWith("image/"));
    if (images.length > 0) {
      event.preventDefault();
      addFiles(images);
    }
  };

  const send = async () => {
    const payload = buildErrorReport({
      title: title ?? "Problema reportado pela pessoa",
      message,
      note,
      area,
      context,
    });
    setFallbackText(formatReportText(payload));
    setStatus("sending");
    try {
      const response = await sendErrorReport(payload, files);
      setResult(response);
      setStatus(response.emailed ? "sent" : "failed");
    } catch {
      setStatus("failed");
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fallbackText);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {status === "sent"
              ? "Enviado à equipa"
              : isFree
                ? "Reportar um problema"
                : "Enviar este problema à equipa"}
          </DialogTitle>
          <DialogDescription>
            {status === "sent"
              ? "Recebemos o teu relatório. A equipa responde-te por e-mail."
              : "Escreve o que se passa e, se ajudar, junta uma captura de ecrã ou o ficheiro que deu erro. A equipa recebe por e-mail o que aconteceu, onde, quem és e o registo técnico da página."}
          </DialogDescription>
        </DialogHeader>

        {status === "sent" && (
          <div
            role="status"
            className="flex gap-3 rounded-xl border border-emerald-600/40 px-4 py-3 dark:border-emerald-400/40"
          >
            <CircleCheck
              className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"
              strokeWidth={1.75}
              aria-hidden
            />
            <div>
              <p className="text-[13px] leading-relaxed">
                Número do relatório:{" "}
                <strong className="font-medium tabular-nums">{result?.report_id}</strong>.
                {user?.email && <> A resposta chega ao teu e-mail ({user.email}).</>}
              </p>
              {result?.detail && (
                <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{result.detail}</p>
              )}
            </div>
          </div>
        )}

        {status === "failed" && (
          <div role="alert" className="space-y-3 rounded-xl border border-destructive/40 px-4 py-3">
            <div className="flex gap-3">
              <CircleAlert
                className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
                strokeWidth={1.75}
                aria-hidden
              />
              <div className="space-y-1">
                <p className="text-sm font-medium">Não consegui enviar o e-mail</p>
                <p className="text-[13px] leading-relaxed text-muted-foreground">
                  {result?.report_id
                    ? `O relatório ${result.report_id} ficou guardado no servidor, mas o e-mail não saiu. `
                    : "Não consegui falar com o servidor. "}
                  Copia o relatório e envia-o por e-mail à equipa
                  {files.length > 0 ? ", juntando os ficheiros à mão" : ""}.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 rounded-lg text-xs"
              onClick={copy}
            >
              <Copy className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
              {copied ? "Copiado!" : "Copiar relatório"}
            </Button>
          </div>
        )}

        {status !== "sent" && (
          <>
            <div className="space-y-2">
              <Label htmlFor="report-note" className="text-sm font-medium">
                {isFree ? "O que se passa?" : "O que estava a fazer? (opcional)"}
              </Label>
              <Textarea
                id="report-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onPaste={onPaste}
                placeholder={
                  isFree
                    ? "Descreve o problema com as tuas palavras. Podes colar aqui uma captura de ecrã."
                    : "Por exemplo: estava a carregar o ficheiro do cliente e apareceu este aviso."
                }
                maxLength={2000}
                rows={4}
                disabled={status === "sending"}
              />
            </div>

            <div className="space-y-2">
              <input
                ref={fileInput}
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                className="sr-only"
                onChange={(e) => {
                  addFiles(Array.from(e.target.files ?? []));
                  e.target.value = "";
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 gap-1.5 rounded-lg text-[13px]"
                onClick={() => fileInput.current?.click()}
                disabled={status === "sending" || files.length >= MAX_ATTACHMENTS}
              >
                <Paperclip className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                Juntar ficheiros
              </Button>
              <p className="text-xs text-muted-foreground">
                Até {MAX_ATTACHMENTS} ficheiros, {formatBytes(MAX_ATTACHMENTS_BYTES)} no total
                (capturas de ecrã, PDF, Excel, CSV). Vão por e-mail para a equipa.
              </p>
              {fileError && (
                <p role="alert" className="text-[13px] text-destructive">
                  {fileError}
                </p>
              )}
              {files.length > 0 && (
                <ul className="space-y-1.5">
                  {files.map((file, index) => (
                    <li
                      key={`${file.name}-${index}`}
                      className="flex items-center gap-2 rounded-lg border border-border/60 px-3 py-1.5 text-[13px]"
                    >
                      <Paperclip
                        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                        strokeWidth={1.75}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate">{file.name}</span>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {formatBytes(file.size)}
                      </span>
                      <button
                        type="button"
                        onClick={() => setFiles(files.filter((_, i) => i !== index))}
                        disabled={status === "sending"}
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/6 hover:text-foreground"
                      >
                        <X className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                        <span className="sr-only">Tirar {file.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {user?.email && (
              <p className="text-xs text-muted-foreground">
                A equipa responde-te para o teu e-mail:{" "}
                <span className="text-foreground">{user.email}</span>
              </p>
            )}
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {status === "sent" ? (
            <Button onClick={() => onOpenChange(false)}>Fechar</Button>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={status === "sending"}
              >
                Cancelar
              </Button>
              <Button onClick={send} disabled={!canSend} className="gap-1.5">
                <Send className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                {status === "sending"
                  ? "A enviar…"
                  : status === "failed"
                    ? "Tentar outra vez"
                    : "Enviar à equipa"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
